import { useEffect, useRef, useState } from 'react';
import { Text, Button, Card, Subtitle1, Switch, } from '@fluentui/react-components';
import { CheckmarkCircleRegular, DismissRegular, ErrorCircleRegular, QuestionRegular } from "@fluentui/react-icons";
import {
    MessageBar,
    MessageBarActions,
    MessageBarTitle,
    MessageBarBody,
    MessageBarGroup,
    MessageBarIntent,
    makeStyles,
    shorthands,
} from "@fluentui/react-components";
import { PublicClientApplication, InteractionRequiredAuthError, AuthError, AuthenticationResult } from "@azure/msal-browser";

import { msalConfigGraph, loginRequestGraph } from '../authConfigGraph';
import { useSignInState } from '../signInState';
import authErrorHandling from '../authErrorHandling';
import useFetch from '../../Hooks/useFetchAPI';
import { FeatureSkeleton } from '../LoadingSkeletons';
import ModuleConfigTable from './ModuleConfigTable';
import { useConfig } from '../../context/ConfigContext';
import AdminLink from './HelperComponents/AdminLink';

const intents: MessageBarIntent[] = ["info", "warning", "error", "success"];

interface NotificationMessage {
    intent: MessageBarIntent;
    id: number;
    message: string;
};

const useStyles = makeStyles({
    messageBarGroup: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...shorthands.overflow("auto"),
    },
});


const ModuleWUfBReporting = () => {
    const styles = useStyles();
    const { appName, knowledgeBaseUrl } = useConfig();
    const [authError, setauthError] = useState<boolean>(false);
    const [connectorTestError, setConnectorTestError] = useState<boolean>(false);
    const { signInState } = useSignInState();
    const featureName = 'IntuneReporting';
    const connectorName = 'WUfB';
    const counterRef = useRef(0);

    const [featureConfigData, setFeatureConfigData] = useState<WUfBReportingFeatureConfigData[] | null>(null);
    const [wUfBReportingConfigItems, setWUfBReportingConfigItems] = useState<ModuleConfigTableProps[]>([]);
    const { fetchAPI: fetchConfig, apiData, isLoading, serverError } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: updateDatabase, apiData: submitData, isLoading: isLoadingSubmit, serverError: serverErrorSubmit } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: testConfig, apiData: testConfigData, isLoading: isLoadingTestConfig, serverError: serverErrorTestConfig } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: enableDataSync, apiData: enableDataSyncData, isLoading: isLoadingEnableDataSync, serverError: serverErrorEnableDataSync } = useFetch({ scope: 'AdminUser' })

    const [messages, setMessages] = useState<NotificationMessage[]>([]);

    const addMessage = (message: string, intent: MessageBarIntent) => {
        const newMessage = { intent, id: counterRef.current++, message };
        setMessages((s) => [newMessage, ...s]);
    };

    const dismissMessage = (messageId: number) =>
        setMessages((s) => s.filter((entry) => entry.id !== messageId)
        );


    useEffect(() => {
        const fetchConfigAsync = async () => {
            await fetchConfig({ url: `admin/getfeatureconfig?featureName=${featureName}`, method: 'GET' });
        };

        fetchConfigAsync()
    }, [signInState.currentCustomerID]);

    const msalInstanceGraph = new PublicClientApplication(msalConfigGraph(process.env.REACT_APP_CLIENT_GRAPH_APP_ID!));

    const handleAppRegConsent = async () => {
        await msalInstanceGraph.initialize();
        await msalInstanceGraph.loginPopup(loginRequestGraph)
            .then(response => {
                // Handle the response
                setauthError(false);
            })
            .catch(error => {
                // Handle the error
                console.log('[consentAppReg] Error:', error);
                setMessages([]);
                setauthError(true);
                addMessage(authErrorHandling(error), 'error');
            });
    };

    const updateConfiguration = async (key: string, value: string | number | boolean | Date) => {
        await updateDatabase({ url: `admin/updatefeatureconfig?featureName=${featureName}&${key}=${value}`, method: 'PUT' });
        setFeatureConfigData((prev) => {
            if (!prev) return prev; // Return unchanged if prev is falsy
            return prev.map(item =>
                item.hasOwnProperty(key) ? { ...item, [key]: value } : item
            );
        });
        setWUfBReportingConfigItems((prev) => {
            if (!prev) return prev; // Return unchanged if prev is falsy
            return prev.map(item =>
                item.hasOwnProperty(key) ? { ...item, [key]: value } : item
            );
        });

        if (key === 'WUfBEnabled') {
            await enableDataSync({ url: 'admin/enabledatasync', method: 'POST', body: { WUfBEnabled: value, } });
        }

    }

    useEffect(() => {
        if (featureConfigData) {
            testConfig({ url: `admin/testfeatureconfig?featureName=${featureName}&connectorName=${connectorName}`, method: 'GET' });
        }
    }, []);

    const testConfiguration = async () => {
        console.log('[ModuleConfig-WUfB] Testing configuration');
        setMessages([]);
        await testConfig({ url: `admin/testfeatureconfig?featureName=${featureName}&connectorName=${connectorName}`, method: 'GET' });
    }

    useEffect(() => {
        if (testConfigData) {
        (async () => {
            console.log('[ModuleConfig-WUfB] testResult:', testConfigData);
            if (testConfigData?.result) {
                console.log('[ModuleConfig-WUfB] Configuration test successful');
                updateConfiguration('WUfBStatus', 'Configured');
                updateConfiguration('WUfBStatusMessage', 'Tested Successfully');
                setConnectorTestError(false);
            }
            else {
                console.log('[ModuleConfig-WUfB] Configuration test failed');
                updateConfiguration('WUfBStatus', 'Not Configured');
                updateConfiguration('WUfBStatusMessage', 'Test Failed');
                updateConfiguration('WUfBEnabled', false);
                setMessages([]);
                setConnectorTestError(true);
                addMessage(`${testConfigData.error}`, 'error');
            }
        })();
    }
    }, [testConfigData]);

    useEffect(() => {
        if (!isLoading && !serverError && apiData) {
            console.log('[ModuleConfig-WUfB] apiData', apiData)
            if (apiData && Array.isArray(apiData.result) && apiData.result.length > 0 && (apiData as unknown as { result: WUfBReportingFeatureConfigData[] }).result) {
                let featureConfigDataResult: WUfBReportingFeatureConfigData[] | null = null;
                let wUfBReportingConfigItemsResult: ModuleConfigTableProps[] = [];
                featureConfigDataResult = (apiData as unknown as { result: WUfBReportingFeatureConfigData[] }).result;
                // console.log('featureConfigDataResult: ', featureConfigDataResult);
                setFeatureConfigData(featureConfigDataResult);

                if (Array.isArray(featureConfigDataResult)) {
                    wUfBReportingConfigItemsResult = featureConfigDataResult.flatMap(item => ([
                        { key: 'WUfBWorkspaceID', displayName: 'WUfB Log Analytics Workspace ID', value: item.WUfBWorkspaceID },
                    ]));
                }
                // console.log('IntuneReportingConfigItemsResult:', IntuneReportingConfigItemsResult);
                setWUfBReportingConfigItems(wUfBReportingConfigItemsResult);
            }
            else {
                console.log('[ModuleConfig-WUfB] apiData is empty');
                let featureConfigDataResult: WUfBReportingFeatureConfigData[] = [{
                    AppFeatureID: 1,
                    WUfBEnabled: false,
                    WUfBWorkspaceID: '',
                    SyncSchedule2: '',
                    WUfBStatus: '',
                    WUfBStatusMessage: '',
                    WUfBLastSyncEnd: null,
                    WUfBLastSyncDuration: null,
                }];
                setFeatureConfigData(featureConfigDataResult);
                let wUfBReportingConfigItemsResult: ModuleConfigTableProps[] = [
                    { key: 'WUfBWorkspaceID', displayName: 'WUfB Log Analytics Workspace ID', value: '' },
                ];
                setWUfBReportingConfigItems(wUfBReportingConfigItemsResult);
            }
        }
    }, [isLoading, serverError, apiData]);

    return (
        <div className="WUfBConfig" style={{ margin: '20px', maxWidth: '1200px' }}>
            {isLoading && !serverError && !apiData &&
                <Card>
                    <Subtitle1>Windows Update for Business Reporting Connector Setup</Subtitle1>
                    <FeatureSkeleton />
                </Card>

            }
            {!isLoading && !serverError && featureConfigData && apiData && wUfBReportingConfigItems &&
                <Card>
                    <Subtitle1>Windows Update for Business Reporting Connector Setup</Subtitle1>
                    <Text>An Entra ID Global Admin or Application Administrator will need to grant consent to the {appName}-GraphSync app to allow access to your data for WUfB reporting.</Text>
                    <Text>Ensure you have manually granted read permissions to the {appName}-GraphSync App (Service Principal) to your Log Analytics Workspace.</Text>
                    <Text>Please see the following Information:</Text>
                    <ul>
                        <li>
                            <AdminLink href={knowledgeBaseUrl} target="_blank">
                                Connecting {appName} to Intune and Windows Update for Business Reports
                            </AdminLink>
                        </li>
                        <li>
                            <AdminLink href="https://learn.rimo3.com/knowledge-base/configure-wufb-log-analytics-permissions" target="_blank">
                                Configure WUfB Log Analytics Permissions
                            </AdminLink>
                        </li>
                    </ul>
                    <Switch
                        key={"WUfBEnabled"}
                        label={"Connector " + (featureConfigData[0].WUfBEnabled ? "enabled" : "disabled")}
                        // labelPosition="before"
                        checked={featureConfigData[0].WUfBEnabled}
                        disabled={!(
                            featureConfigData[0]?.WUfBWorkspaceID &&
                            featureConfigData[0]?.WUfBStatusMessage === "Tested Successfully"
                        )}
                        onChange={(e) => updateConfiguration("WUfBEnabled", e.target.checked)}
                    />

                    <Text>{featureConfigData[0]?.WUfBStatus ? `Connector Status: ${featureConfigData[0]?.WUfBStatus}` : null}</Text>
                    <Text>{featureConfigData[0]?.WUfBStatusMessage ? `Connector Status Message: ${featureConfigData[0]?.WUfBStatusMessage}` : null}</Text>
                    <Text>{featureConfigData[0]?.WUfBLastSyncEnd ? `Connector Last Sync End Date: ${new Date(featureConfigData[0]?.WUfBLastSyncEnd).toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : `Connector Last Sync End Date: N/A`}</Text>
                    <Text>
                        {featureConfigData[0]?.WUfBLastSyncDuration == null
                            ? 'Connector Last Sync Duration: N/A'
                            : `Connector Last Sync Duration: ${featureConfigData[0]?.WUfBLastSyncDuration === 0
                                ? '< 1 Min'
                                : `${featureConfigData[0]?.WUfBLastSyncDuration} mins`
                            }`}
                    </Text>

                    < ModuleConfigTable<ModuleConfigTableProps[]> data={wUfBReportingConfigItems} appFeature={featureName} onUpdate={updateConfiguration} />

                    <div style={{ minWidth: '50%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                        < Button style={{ width: '100%' }} onClick={handleAppRegConsent} appearance='primary'> Grant Consent </Button>
                    </div>
                    <div style={{ width: '50%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                        <Button style={{ width: '100%' }} onClick={testConfiguration}
                            icon={featureConfigData[0].WUfBStatusMessage === 'Tested Successfully'
                                ? <CheckmarkCircleRegular style={{ color: 'green' }} />
                                : <ErrorCircleRegular style={{ color: 'red' }} />}
                        >
                            Test Configuration
                        </Button>
                    </div>
                    {/* <br/> */}
                    {(authError || connectorTestError) &&
                        <MessageBarGroup className={styles.messageBarGroup}>
                            {messages.map(({ intent, id, message }) => (
                                <MessageBar key={`${intent}-${id}`} intent={intent}>
                                    <MessageBarBody>
                                    {authError && <MessageBarTitle>Error granting access:</MessageBarTitle>}
                                    {connectorTestError && <MessageBarTitle>Testing Error:</MessageBarTitle>}
                                        {message}
                                    </MessageBarBody>
                                    <MessageBarActions
                                        containerAction={
                                            <Button
                                                onClick={() => dismissMessage(id)}
                                                aria-label="dismiss"
                                                appearance="transparent"
                                                icon={<DismissRegular />}
                                            />
                                        }
                                    />
                                </MessageBar>
                            ))}
                        </MessageBarGroup>
                    }
                </Card>
            }
            {serverError &&
                // Put in nice pretty error handling component when it's created
                <>Server Error: {serverError}</>
            }

        </div>
    );

};

export default ModuleWUfBReporting;
